import { asDate, type DateLike } from './timezoneUtils'

/**
 * ATL-1528 — the shifts of a store, reconstructed from its cashbox rows.
 *
 * THE INVARIANT: a store has at most ONE shift per business day, and that shift IS the day's
 * dailyStats doc. ShiftScreen offers «Open shift» only while that doc is not `shiftOpened`
 * (ShiftScreen.js), and the till books its open, its deposits/withdrawals and its close to the doc
 * named by `getDailyStatID(store, now)` — the id every row stores as `dailyStatID`. `shiftClosed`,
 * `shiftClosedTime` and every `…EndAmount` live on that doc, once per day.
 *
 * So rows are grouped by the dailyStat id they were BOOKED to: the stored `dailyStatID`, or — for a
 * row that does not carry one (the Supabase read, legacy rows) — the id the writer's own rule gives
 * for the row's instant. Everything else the report used to key on was a proxy that broke:
 *
 *  - the CALENDAR day of each row split a shift closed after local midnight into an open-only card
 *    (which crashed the app) and a close-only card, and offered to close it again — 218 duplicate
 *    closeShift rows in production (ATL-1531);
 *  - pairing each open with the next close turned a duplicate open (ATL-1338: one shift opened
 *    nine times) into eight phantom «not closed» shifts.
 *
 * Within a day: the shift's open is the day's LAST open — an open SETS the day's `…StartAmount`
 * and `shiftOpenedTime` (merge), so the last one is what the books hold. Its close is the till's
 * close. The report's own «Close shift» stamps its close with an OPEN's instant, so such a
 * backdated close says nothing about when it was written and carries system amounts, not a count;
 * when the day also has a real close, the real one is the shift's close. Every other open and close
 * of the day is a duplicate and is returned as such — surfaced, never dropped.
 *
 * A close cannot precede its shift's open. A close booked to day X but written before X's first
 * open (or on a day with no open) is the late close of the PREVIOUS shift: the till screen was left
 * open past the autoclose cutoff, so its day rule named the next day. It closes that shift when the
 * shift is open, unclosed (or closed only by the report's backdated close, which it then outranks,
 * as on one day) and opened within `LATE_CLOSE_WINDOW_MS` — never a stale shift days back.
 */

export type CashboxRowKind = 'open' | 'close' | 'other'

interface CashboxRowLike {
  id?: unknown
  type?: unknown
  date?: unknown
  store?: unknown
  dailyStatID?: unknown
}

export interface CashboxShift<T> {
  /** The business day's dailyStat id: the shift's identity. */
  key: string
  open: T | null
  close: T | null
  /** Further opens booked to the same day (ATL-1338 duplicate opens). */
  extraOpens: T[]
  /** Further closes booked to the same day. The books should hold one. */
  extraCloses: T[]
  /** The open's instant, else the close's — for ordering shifts. */
  anchor: Date
  store: string | null
}

/** The dailyStat id the writer's rule gives a store and instant (`getDailyStatID`). */
export type DailyStatIdOf = (storeId: string, date: Date) => string | null

/** Rows carry `{ id, text }`; the oldest only the Russian `text`; a few a bare id string. */
export const cashboxRowKind = (row: CashboxRowLike | null | undefined): CashboxRowKind => {
  const type = row?.type
  const id = typeof type === 'string' ? type : (type as { id?: unknown } | null | undefined)?.id
  const text = typeof type === 'object' && type !== null ? (type as { text?: unknown }).text : undefined
  if (id === 'openShift' || text === 'Открытие смены' || type === 'Открытие смены') return 'open'
  if (id === 'closeShift' || text === 'Закрытие смены' || type === 'Закрытие смены') return 'close'
  return 'other'
}

/** The row's store as its id string: Firestore stores the id, a Supabase lookup `{ id }`. */
export const cashboxRowStoreId = (store: unknown): string | null => {
  if (typeof store === 'string' && store) return store
  if (store && typeof store === 'object' && typeof (store as { id?: unknown }).id === 'string') {
    return (store as { id: string }).id || null
  }
  return null
}

interface Dated<T> {
  row: T
  date: Date
}

/** How long after an open a late close (booked to the next day) is still taken as its close. */
const LATE_CLOSE_WINDOW_MS = 48 * 60 * 60 * 1000

/**
 * @param rows any cashbox rows; deposits and withdrawals are read past (the report shows shifts)
 * @param dailyStatIdOf the writer's day rule, for rows that do not store their day
 * @param fallbackStoreId the store queried, for a row that does not name its own
 * @returns `shifts` oldest first, and `unplaced` — open/close rows whose day or instant cannot be
 *   told. The caller reports them; they are never guessed into a shift.
 */
export const groupCashboxShifts = <T extends CashboxRowLike>(
  rows: T[],
  dailyStatIdOf: DailyStatIdOf,
  fallbackStoreId: string | null = null
): { shifts: CashboxShift<T>[]; unplaced: T[] } => {
  const unplaced: T[] = []
  const days = new Map<string, { store: string | null; opens: Dated<T>[]; closes: Dated<T>[] }>()

  for (const row of rows) {
    const kind = cashboxRowKind(row)
    if (kind === 'other') continue
    const date = asDate(row?.date as DateLike)
    const store = cashboxRowStoreId(row?.store) ?? fallbackStoreId
    const stored = typeof row?.dailyStatID === 'string' && row.dailyStatID ? row.dailyStatID : null
    const key = stored ?? (date && store ? dailyStatIdOf(store, date) : null)
    // A row the report cannot order within its day is as unusable as one it cannot place.
    if (!key || !date) {
      unplaced.push(row)
      continue
    }
    const day = days.get(key) ?? { store, opens: [], closes: [] }
    ;(kind === 'open' ? day.opens : day.closes).push({ row, date })
    days.set(key, day)
  }

  const byTime = (a: Dated<T>, b: Dated<T>) => a.date.getTime() - b.date.getTime()
  interface Built {
    shift: CashboxShift<T>
    lastOpen: Date | null
    /** Closes written before the day's first open, or on a day with no open. */
    early: Dated<T>[]
    /** The shift's close is the report's, backdated to an open: a real (late) close outranks it. */
    closeBackdated: boolean
  }
  const built: Built[] = []
  for (const [key, day] of days) {
    day.opens.sort(byTime)
    day.closes.sort(byTime)
    const firstOpen = day.opens[0]?.date ?? null
    const lastOpen = day.opens[day.opens.length - 1] ?? null
    const openTimes = new Set(day.opens.map((o) => o.date.getTime()))
    const early = firstOpen ? day.closes.filter((c) => c.date < firstOpen) : day.closes
    const own = day.closes.filter((c) => !early.includes(c))
    const backdated = (c: Dated<T>) => openTimes.has(c.date.getTime())
    const close = own.find((c) => !backdated(c)) ?? own[0] ?? null
    built.push({
      shift: {
        key,
        open: lastOpen?.row ?? null,
        close: close?.row ?? null,
        extraOpens: day.opens.slice(0, -1).map((d) => d.row),
        extraCloses: own.filter((c) => c !== close).map((d) => d.row),
        anchor: (day.opens[0] ?? day.closes[0]).date,
        store: day.store,
      },
      lastOpen: lastOpen?.date ?? null,
      early,
      closeBackdated: close !== null && backdated(close),
    })
  }
  built.sort((a, b) => a.shift.anchor.getTime() - b.shift.anchor.getTime())

  // Late closes: give each day's early closes to the previous shift of the same store when it is
  // the shift they close; otherwise they stay with their own day (a close-only day, or duplicates).
  // As within a day, the till's close outranks the report's close backdated to the open: a shift
  // closed from the report and then by the till past the cutoff is closed by the till.
  const lastByStore = new Map<string, Built>()
  const keep: Built[] = []
  for (const b of built) {
    const storeKey = b.shift.store ?? ''
    const previous = lastByStore.get(storeKey)
    let early = b.early
    if (early.length > 0 && previous?.lastOpen && (previous.shift.close === null || previous.closeBackdated)) {
      const first = early[0]
      const age = first.date.getTime() - previous.lastOpen.getTime()
      if (age >= 0 && age <= LATE_CLOSE_WINDOW_MS) {
        if (previous.shift.close !== null) previous.shift.extraCloses.push(previous.shift.close)
        previous.shift.close = first.row
        previous.closeBackdated = false
        previous.shift.extraCloses.push(...early.slice(1).map((d) => d.row))
        early = []
      }
    }
    if (early.length > 0) {
      if (b.shift.open === null && b.shift.close === null) {
        b.shift.close = early[0].row
        b.shift.extraCloses.push(...early.slice(1).map((d) => d.row))
      } else {
        b.shift.extraCloses.push(...early.map((d) => d.row))
      }
    }
    // A day whose only rows were a late close given away has nothing left to show.
    if (b.shift.open !== null || b.shift.close !== null) {
      keep.push(b)
      lastByStore.set(storeKey, b)
    }
  }
  // Already oldest first: `built` was sorted by anchor, and giving a close away moves no anchor.
  return { shifts: keep.map((b) => b.shift), unplaced }
}
